import { Injectable } from '@angular/core';
import { MapHelperService } from 'src/services/map-helper.service';
import { FeatureCollection, Polygon, MultiPolygon } from '@turf/helpers';
import { environment } from 'src/environments/environment';
import { filter, firstValueFrom, take } from 'rxjs';
import {PolygonSelectionEvent } from '../models/polygon-selector.models';
import { CloropetClases, CloropetConfiguration } from 'src/models/CloropetClases';
import { EnumPolygonFillType } from '../helpers/enums/EnumPolygonFillType.enum';

@Injectable()
export class PolygonSelectorService {

  selectedFeatures: FeatureCollection<Polygon | MultiPolygon> = { type: 'FeatureCollection', features: [] };
  
  // para configuracion
  multipleSelection: boolean = false;
  sourceName: string = 'polygon-selector-source-';
  layerName: string = 'layer-polygon-selector-';
  layerLineName: string = 'layer-line-polygon-selector-';
  layerTextName: string = 'layer-polygon-selector-text-';
  fillColor: string = '#627e90';
  lineColor: string = '#352F84';
  textProperty: string = 'Description';
  cloropetConfig: CloropetConfiguration = null;

  private hoveredPolygonId = null;

  private onSelectionChangeCallback: ((event: PolygonSelectionEvent) => void) | null = null;

  isInitialized: boolean = false;
  isActive: boolean = false;

  constructor(private mapHelperService: MapHelperService) {

  }

  configure(sourceNameAdd: string){
    if (!sourceNameAdd || sourceNameAdd.trim() == '')
        return;

    this.sourceName = this.sourceName + sourceNameAdd;
    this.layerName = this.layerName + sourceNameAdd;
    this.layerLineName = this.layerLineName + sourceNameAdd;
    this.layerTextName = this.layerTextName + sourceNameAdd;
  }

  onSelectionChange(callback: (event: PolygonSelectionEvent) => void): void {
    this.onSelectionChangeCallback = callback;
  }

  private notifySelectionChange() {
    if (this.onSelectionChangeCallback) {
      this.onSelectionChangeCallback({ selectedFeatures: { ...this.selectedFeatures, features: [...this.selectedFeatures.features] }});
    }
  }
  
  async initialize(fillType: string, skipLayerCreation: boolean = false, canSelect: boolean = true) {
    if (this.mapHelperService._map && this.mapHelperService.mainSourceSubject.value?.isMainSourceReady && this.mapHelperService.mainSourceSubject.value?.isStyleReady) {
      if (!skipLayerCreation) {
        this.createSourceAndLayers(fillType, canSelect);
      } else {
        if(canSelect)
          this.setupEventHandlers();

        this.isInitialized = true;
      }
      return;
    }

    try {
      await firstValueFrom(
        this.mapHelperService.mainSource$.pipe(
          filter(state => state != null && state.isMainSourceReady && state.isStyleReady),
          take(1)
        )
      );
      if (!skipLayerCreation) {
        this.createSourceAndLayers(fillType, canSelect);
      } else {
        if(canSelect)
          this.setupEventHandlers();
      
        this.isInitialized = true;
    }
    } catch (e) {
      console.log('polygon selector, error esperando al mapa', e);
    }
  }

  private createSourceAndLayers(fillType: string, canSelect: boolean = true): void {
    if (this.isInitialized) 
      return;

    const existingSource = this.mapHelperService.GetSourceByName(this.sourceName);
    
    if (!existingSource) {
      switch(fillType) {
        case EnumPolygonFillType.Cloropet:
          this.mapHelperService.AddSourceEmptyGeoJson(this.sourceName, "Polígonos selector", 'annotation-polygon-selector.png');
          this.mapHelperService.AddLayerPolygonsWithFeatureStatus(this.layerName, this.sourceName, "Polígonos selector", this.fillColor, this.cloropetConfig);
          this.mapHelperService.AddLayerText(this.layerTextName, this.sourceName, this.textProperty, "Etiqueta de polígonos", -1);
          break;
        case EnumPolygonFillType.Solid:
          this.mapHelperService.AddSourceEmptyGeoJson(this.sourceName, "Polígonos selector", 'annotation-cloropet-polygon-selector.png');
          this.mapHelperService.AddLayerPolygonsWithFeatureStatus(this.layerName, this.sourceName, "Polígonos selector", this.fillColor);
          this.mapHelperService.AddLayerLine(this.layerLineName, this.sourceName, "Líneas de polígonos", this.lineColor, 3);
          this.mapHelperService.AddLayerText(this.layerTextName, this.sourceName, this.textProperty, "Etiqueta de polígonos", -1);
        default:
          this.mapHelperService.AddSourceEmptyGeoJson(this.sourceName, "Polígonos selector", 'annotation-polygon-selector.png');
          this.mapHelperService.AddLayerPolygonsWithFeatureStatus(this.layerName, this.sourceName, "Polígonos selector", this.fillColor);
          this.mapHelperService.AddLayerLine(this.layerLineName, this.sourceName, "Líneas de polígonos", this.lineColor, 3);
          this.mapHelperService.AddLayerText(this.layerTextName, this.sourceName, this.textProperty, "Etiqueta de polígonos", -1);
          break;
      }
      
    }

    if(canSelect)
      this.setupEventHandlers();

    this.isInitialized = true;
  }
  
  private setupEventHandlers(): void {
    this.isInitialized = true;
    this.activate();
  }

  activate(): void {
    if (!this.isInitialized || this.isActive) 
      return;

    this.mapHelperService._map.on('click', this.layerName, this.handlePolygonClick);
    this.mapHelperService._map.on('mousemove', this.layerName, this.handlePolygonHover);
    this.mapHelperService._map.on('mouseleave', this.layerName, this.handlePolygonUnhover);
    
    this.isActive = true;

  }

  deactivate(): void {
    if (!this.isActive) 
      return;

    this.mapHelperService._map.off('click', this.layerName, this.handlePolygonClick);
    this.mapHelperService._map.off('mousemove', this.layerName, this.handlePolygonHover);
    this.mapHelperService._map.off('mouseleave', this.layerName, this.handlePolygonUnhover);
    
    this.isActive = false;

    if (!environment.production) {
      console.log('PolygonSelector: Desactivado');
    }
  }


  loadGeoJson(geoJson: FeatureCollection<Polygon | MultiPolygon>): void {
    if (!this.isInitialized) {
      console.warn('olygon selector, no inicializado');
      return;
    }

    if (!geoJson?.features) {
      console.warn('polygon selector, geojson inválido, no se carga');
      return;
    }

    // crea id si no existen
    geoJson.features.forEach((feature, index) => {
      if (feature.id == undefined || feature.id == null) {
        feature.id = index + 1;
      }
    });

    this.mapHelperService.UpdateLayerGeoJson(this.sourceName, geoJson);
    this.showLayers();
  }

  clearGeoJson(): void {
    this.mapHelperService.UpdateLayerGeoJson(this.sourceName, this.mapHelperService.Empty_GeoJson);
    this.clearSelection();
  }


  showLayers(): void {
    this.mapHelperService.ShowLayer(this.layerName);
    this.mapHelperService.ShowLayer(this.layerLineName);
    this.mapHelperService.ShowLayer(this.layerTextName);
  }

  hideLayers(): void {
    this.mapHelperService.HideLayer(this.layerName);
    this.mapHelperService.HideLayer(this.layerLineName);
    this.mapHelperService.HideLayer(this.layerTextName);
  }

  sendToTop(): void {
    this.mapHelperService.MoveLayerToTop(this.layerName);
    this.mapHelperService.MoveLayerToTop(this.layerLineName);
    this.mapHelperService.MoveLayerToTop(this.layerTextName);
  }
  // #endregion

  // #region pra los estados de seleccion
  selectById(id: string | number): void {
    if (!this.mapHelperService._map.getLayer(this.layerName)) return;

    const features = this.mapHelperService.getLayerFeatures(this.layerName);
    const feature = features.find(f => f.id === id);
    
    if (feature) {
      if (!this.multipleSelection) {
        this.deselectAll(false);
      }
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: feature.id }, { selected: true });
      this.selectedFeatures.features.push(feature);
      this.notifySelectionChange();
    }
  }

  selectByProperty(key: string, value: string): void {
    if (!this.mapHelperService._map.getLayer(this.layerName)) return;

    const features = this.mapHelperService.getLayerFeatures(this.layerName);
    const feature = features.find(f => f.properties && f.properties[key] === value);
    
    if (feature) {
      if (!this.multipleSelection) {
        this.deselectAll(false);
      }
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: feature.id }, { selected: true });
      this.selectedFeatures.features.push(feature);
      this.notifySelectionChange();
    }
  }

  selectAll(): void {
    if (!this.mapHelperService._map.getLayer(this.layerName)) return;

    const features = this.mapHelperService.getLayerFeatures(this.layerName);
    features.forEach((feature) => {
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: feature.id }, { selected: true });
    });
    this.selectedFeatures = { type: 'FeatureCollection', features: [...features] };
    this.notifySelectionChange();
  }

  deselectAll(notify: boolean = true): void {
    if (!this.mapHelperService._map.getLayer(this.layerName)) return;

    const features = this.mapHelperService.getLayerFeatures(this.layerName);
    features.forEach((feature) => {
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: feature.id }, { selected: false });
    });
    this.selectedFeatures = { type: 'FeatureCollection', features: [] };
    
    if (notify) {
      this.notifySelectionChange();
    }
  }

  clearSelection(): void {
    this.deselectAll(true);
  }

  getSelectedFeatures(): FeatureCollection<Polygon | MultiPolygon> {
    return { ...this.selectedFeatures, features: [...this.selectedFeatures.features] };
  }
  // #endregion

  // #region eventos de las capas
  private handlePolygonClick = (e: any): void => {
    if (!e.features?.length) return;

    const feature = e.features[0];
    const featureId = feature.id;

    const isSelected = this.mapHelperService._map.getFeatureState({ source: this.sourceName, id: featureId }).selected;

    if (!isSelected) {
      // Seleccionar
      if (!this.multipleSelection) {
        this.deselectAll(false);
      }
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: featureId }, { selected: true });
      this.selectedFeatures.features.push(feature);
      this.notifySelectionChange();

      if (!environment.production) {
        console.log('polygon selector, Seleccionado', feature);
      }
    } else {
      // Deseleccionar
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: featureId }, { selected: false });
      this.selectedFeatures.features = this.selectedFeatures.features.filter(f => f.id !== featureId);
      this.notifySelectionChange();

      if (!environment.production) {
        console.log('polygon selector, Deseleccionado', feature);
      }
    }
  }

  private handlePolygonHover = (e: any): void => {
    if (!e.features?.length) return;
    if (e.features[0].layer.id !== this.layerName) return;

    if (this.hoveredPolygonId != null) {
      this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: this.hoveredPolygonId }, { hover: false });
    }

    const feature = e.features[0];
    this.hoveredPolygonId = feature.id;

    this.mapHelperService._map.getCanvas().style.cursor = 'pointer';
    this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: this.hoveredPolygonId }, { hover: true });
  }

  private handlePolygonUnhover = (e: any): void => {
    if (this.hoveredPolygonId == null) return;

    this.mapHelperService._map.getCanvas().style.cursor = '';
    this.mapHelperService._map.setFeatureState({ source: this.sourceName, id: this.hoveredPolygonId }, { hover: false });
    this.hoveredPolygonId = null;
  }
  // #endregion

  destroy(): void {
    this.multipleSelection = false;
    this.deactivate();
    this.clearGeoJson();
    this.hideLayers();
    this.onSelectionChangeCallback = null;
  }
}
